/**
 * 剪切/拷贝/粘贴（DESIGN.md §6.1 补充，走系统剪贴板——见 clipboard-edit.ts）。
 *
 * 这三个**不**在 ActionDefinition 上挂 `shortcut`：Ctrl+C/X/V 是浏览器里
 * 无处不在的原生编辑键，本应用别的地方也有可编辑输入框（文字工具的
 * 编辑框、命令面板搜索框……），如果走 `registry.actionForKey` 那条通用
 * 匹配路径，会在用户正常复制/粘贴文本时被无差别劫持成"拷贝画布内容"。
 * 快捷键改在 main.tsx 里手动接，先判断焦点是不是可编辑元素，是就放行给
 * 浏览器原生行为——同 [ ]/Backspace 这类键位已经在用的模式，不是新花样。
 * 代价是菜单右侧不会显示 Ctrl+C 这行字（`shortcutText` 纯从 `.shortcut`
 * 派生），鼠标点菜单和快捷键两条路都能用，只是菜单不显示后者。
 */
import type { ActionRegistry } from './registry';
import type { AppStore } from '../store';
import type { ClipboardGateway } from '../../io/clipboard';
import { copyToClipboard, cutToClipboard, pasteFromClipboard, type PasteDeps } from '../clipboard-edit';

export interface ClipboardActionDeps extends PasteDeps {
  readonly store: AppStore;
  readonly clipboard: ClipboardGateway;
}

export function registerClipboardActions(registry: ActionRegistry, deps: ClipboardActionDeps): void {
  const reportFailure = (verb: string, error: unknown, hint: (text: string) => void): void => {
    hint(`${verb}失败：${error instanceof Error ? error.message : String(error)}`);
  };

  registry.register({
    id: 'edit.copy',
    title: '拷贝',
    group: '编辑',
    section: 2,
    order: 1,
    enabled: (s) => s.hasDocument,
    run: async (services) => {
      try {
        if (!(await copyToClipboard(deps.store, deps.clipboard))) services.hint('没有可拷贝的内容');
      } catch (error) {
        reportFailure('拷贝', error, services.hint);
      }
    },
  });

  registry.register({
    id: 'edit.cut',
    title: '剪切',
    group: '编辑',
    section: 2,
    order: 2,
    enabled: (s) => s.hasDocument,
    run: async (services) => {
      try {
        if (!(await cutToClipboard(deps.store, deps.clipboard))) services.hint('没有可剪切的内容');
      } catch (error) {
        reportFailure('剪切', error, services.hint);
      }
    },
  });

  registry.register({
    id: 'edit.paste',
    title: '粘贴',
    group: '编辑',
    section: 2,
    order: 4,
    enabled: (s) => s.hasDocument,
    run: async (services) => {
      try {
        if (!(await pasteFromClipboard(deps.store, deps.clipboard, deps))) {
          services.hint('剪贴板里没有图片');
        }
      } catch (error) {
        reportFailure('粘贴', error, services.hint);
      }
    },
  });
}
